import { StrictMode } from 'react';
import * as React from 'react';

import { getDefaultStore } from 'jotai';
// jotai-devtools must be imported before the jotai store is created (which happens at
// import time inside @consumer-frontend/nc-components) so it can instrument the store;
// otherwise the DevTools panel renders empty. See jotaijs/jotai-devtools#212.
import { DevTools } from 'jotai-devtools';
import * as ReactDOM from 'react-dom/client';
import { createBrowserRouter } from 'react-router-dom';
import { SWRConfig } from 'swr';

import { applySwrConfig, worker } from '@consumer-frontend/nc-api';
import { NcApplyOptimizelyProvider } from '@consumer-frontend/nc-components';
import {
  datadogRumErrorHandling,
  enableOptimizely,
  isMockingEnabled,
} from '@consumer-frontend/nc-utils';

import { App } from './app/App';
import { routes } from './routes';
import './utils/datadog';

// We have to use the build-time `import.meta.env.VITE_ENV` instead of the run-time `isProdEnv` util or `{ VITE_ENV } = import.meta.env`
// to allow Vite to properly tree-shake and prevent Jotai dev tools from being included in prod bundle.
if (import.meta.env.VITE_ENV !== 'production') {
  import('jotai-devtools/styles.css');
}

async function enableMocking() {
  if (isMockingEnabled()) {
    await worker.start({
      // This disables reporting of unhandled requests. Cf: https://github.com/mswjs/msw/discussions/1589
      onUnhandledRequest: 'bypass',
    });
  }
}

async function enableAxe() {
  if (import.meta.env.VITE_ENV === 'development') {
    // eslint-disable-next-line import/no-extraneous-dependencies
    const { default: axe } = await import('@axe-core/react');

    await axe(React, ReactDOM, 1000);
  }
}

Promise.all([
  enableMocking(),
  enableAxe(),
  enableOptimizely(),
]).then(() => {
  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement,
    datadogRumErrorHandling,
  );

  const router = createBrowserRouter(routes, { future: { v8_middleware: true } });

  root.render(
    <StrictMode>
      <SWRConfig value={applySwrConfig}>
        <NcApplyOptimizelyProvider>
          <App router={router} />
        </NcApplyOptimizelyProvider>
      </SWRConfig>

      {import.meta.env.VITE_ENV !== 'production' && <DevTools store={getDefaultStore()} />}
    </StrictMode>,
  );
});
